import { accessControl } from 'lib-shared-access-control';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { pwaMediaQuery } from 'app/middleware/visibility/utils/helpers';
import { hasBooleanFeatures } from 'components/shared/featureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import { useMediaQuery } from '../../../../../../utils/mediaQuery';
import { getSettingsMenuCategories } from '../menuCategories';
import { type MenuCategory } from '../types';

export const useJabraRoutes = (): boolean => {
  const [isOpenedInPWA] = useMediaQuery(pwaMediaQuery);
  return isOpenedInPWA;
};

/**
 * Checks if the user has access to the menu item based on the useCopilotTrial
 * hook results. If the menu item is not part of copilot it will return true.
 *
 * @param id - The id of the menu item.
 * @param hasCopilot - The copilot status of the user.
 * @param eligibleForTrial - The copilot trial status of the user.
 */
const hasCopilotTrialAcccess = (
  id: string,
  hasCopilot: boolean,
  eligibleForTrial: boolean,
) => {
  switch (id) {
    case 'copilotTrial':
      return eligibleForTrial;
    case 'writingStyleGuide':
      return hasCopilot;
    default:
      return true;
  }
};

/** Creates a setting menu depending on the role of current agent. */
export function useSettingsMenu(): readonly MenuCategory[] {
  const { allFeatureFlags } = useAppSelector(
    (state) => ({
      allFeatureFlags: state.settings.profile.featureflags,
    }),
    shallowEqual,
  );
  const { hasCopilot, eligibleForTrial } = useCopilotTrial();

  return useMemo(
    () =>
      getSettingsMenuCategories().reduce((menu, category) => {
        const accessibleMenuItems = category.items.filter((item) => {
          const hasAccess = item.accessControl
            ? accessControl.hasAccess(
                ...(item.accessControl as Parameters<
                  typeof accessControl.hasAccess
                >),
              )
            : true;

          return (
            hasAccess &&
            hasBooleanFeatures(
              allFeatureFlags,
              item.featureFlags,
              item.featureFlagsStrategy,
            ) &&
            hasCopilotTrialAcccess(item.id, hasCopilot, eligibleForTrial)
          );
        });

        if (accessibleMenuItems.length) {
          return [...menu, { ...category, items: accessibleMenuItems }];
        }

        return menu;
      }, [] as readonly MenuCategory[]),
    [allFeatureFlags, hasCopilot, eligibleForTrial],
  );
}
